import { Fragment, useState, useRef } from 'react';
import intl from 'react-intl-universal';

import { StatusCodes } from 'http-status-codes';
import { Typography, Modal, Box } from '@mui/material';
import Stack from '@mui/material/Stack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import api from 'utils/api';
import localData from 'utils/localData';
import useApi from 'shared/hooks/api';
import { PageError, Modal as CustomizedModal } from 'components';
import { ConfirmModal } from 'shared/modals/confirmModal';
import Loader from './utils/Loader';
import Info from './components/Info/kenshinInfo';
import Viewport from './components/Viewport';
import Preview from './components/Preview';
import {
  TopActions,
  TopActionsRight,
  ViewportModalPaper,
  PreviewModalPaper,
} from './Styles';
import { copyToClipboard } from './utils/utils';
import { USER_COMMENT } from 'shared/constants/comment';
import { isCurrentUserTaskManager } from 'utils/privilege';
import { KENSHIN_API } from 'shared/constants/apis';
import { ServiceType } from 'shared/constants/service';

import ConfirmModalWithConsent from 'components/ConfirmModalWithConsent';
import IssueOptionsMenu from './components/IssueOptionsMenu';
import KenshinCaseBatchEditModal from './components/KenshinCaseBatchEditModal';
import Table from './components/KenshinTable';
import { updateArrayItemById } from 'utils/javascript';
import { modalHeights, modalWidths, zIndexValues } from 'utils/styles';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { IssueStatus } from 'shared/constants/issues';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

import logger from 'utils/logger';
import { USER_DATA } from 'shared/constants/users';

const log = logger.createLogger('KenshinIssueDetails');

const KenshinIssueDetails = () => {
  const { issueId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const context = useOutletContext();
  const { doctors, updateLocalProjectIssues } = context;

  const navigate = useNavigate();

  const [openCopyButtonTooltip, setOpenCopyButtonTooltip] = useState(false);
  const [viewportModalOpen, setViewModalOpen] = useState(false);
  const [viewportWebUrls, setViewportWebUrls] = useState([]);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState();
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    source: '',
    destination: '',
  });
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [attachmentObjToBePreviewed] = useState({});

  const [confirmModalWithConsentOpen, setConfirmModalWithConsentOpen] =
    useState(false);
  const [issueShareConfirmModalOpen, setIssueShareConfirmModalOpen] =
    useState(false);
  const shareIssueConfirmModalBodyText = useRef(null);
  const shareIssueConfirmFunc = useRef(null);
  const shareIssueCancelFunc = useRef(null);
  const [selectedCases, setSelectedCases] = useState([]);
  const [batchEditModalIsOpen, setBatchEditModalIsOpen] = useState(false);

  const isTaskManager = isCurrentUserTaskManager();
  const currentOrganizationId = localData.get(USER_COMMENT.CURRENT_ORGID);
  const currentUsername = localData.get(USER_DATA.USERNAME);
  const userId = localData.get(USER_COMMENT.USER_ID);

  const [{ data, error, setLocalData }] = useApi.useGet(
    `${KENSHIN_API.ISSUES}/${issueId}`,
    {
      params: {
        organizationId: currentOrganizationId,
        username: currentUsername,
        userId: userId,
      },
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
    }
  );

  if (error && error.status === StatusCodes.NOT_FOUND) navigate('/home');
  if (!data) return <Loader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { issue } = data;

  const updateLocalCase = (caseId, updatedFields) => {
    setLocalData((currentData) => ({
      issue: {
        ...currentData.issue,
        cases: updateArrayItemById(
          currentData.issue.cases,
          caseId,
          updatedFields
        ),
      },
    }));
  };

  const srcOrgId = issue.srcOrgId.toString();
  const isClient = parseInt(srcOrgId) === parseInt(currentOrganizationId);

  const updateLocalIssueDetails = (fields) => {
    setLocalData((currentData) => ({
      issue: { ...currentData.issue, ...fields },
      // user_quotas: currentData.user_quotas,
      // doctors: currentData.doctors,
    }));
    // the function passed into the brackets of setLocalData(...) is the `getUpdatedData`
    // in setLocalData definition in /shared/hooks/api/query.js;
    // Therefore currentData is `data` in the line `const updatedData = getUpdatedData(data);`;
  };

  const updateIssue = (updatedFields) => {
    api.optimisticUpdate(
      `${KENSHIN_API.ISSUES}/single/${issueId}`,
      {
        updatedFields,
        currentFields: issue,
        setLocalData: (fields) => {
          updateLocalIssueDetails(fields);
          updateLocalProjectIssues(issue.id, fields);
        },
      },
      {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          username: localData.get(USER_DATA.USERNAME),
          userId: localData.get(USER_COMMENT.USER_ID),
        },
        baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      }
    );
  };

  const handleCopyClick = (text) => {
    copyToClipboard(text);
    setOpenCopyButtonTooltip(true);
    setTimeout(() => {
      setOpenCopyButtonTooltip(false);
    }, 2000);
  };

  const closeBatchEditModal = () => {
    setBatchEditModalIsOpen(false);
  };

  const handleDelete = () => {
    api.delete(`${KENSHIN_API.ISSUES}/${issueId}`, {
      params: {
        organizationId: currentOrganizationId,
        userId: userId,
      },
    });
    enqueueSnackbar(intl.get('issue_details_delete_issue_success'), {
      variant: 'success',
    });
    const currentPath = window.location.pathname;
    const newPath = currentPath.split('/issues/')[0];
    navigate(newPath, { state: { shouldRefresh: true } });
  };

  const generateConfirmMessage = (confirmModalInfo) => {
    if (confirmModalInfo.AssignType === 'assign') {
      return intl.get('kanban_confirm_popup_window_text_assign_doctor');
    } else if (confirmModalInfo.AssignType === 'unassign') {
      return intl.get('kanban_confirm_popup_window_text_unassign_doctor');
    }

    if (confirmModalInfo.destination === IssueStatus.DOCTORASSIGNED) {
      return intl.get('kanban_confirm_popup_window_text_doctor_assigned');
    } else if (confirmModalInfo.destination === IssueStatus.REPORTREADY) {
      return intl.get('kanban_confirm_popup_window_text_report_ready');
    } else if (confirmModalInfo.destination === IssueStatus.SENDBACK) {
      return intl.get('kanban_confirm_popup_window_text_send_back');
    } else if (confirmModalInfo.destination === IssueStatus.DONE) {
      return intl.get('kanban_confirm_popup_window_text_done');
    }

    return 'undefined message';
  };

  return (
    <CustomizedModal
      isOpen
      testid="modal:issue-details"
      width={modalWidths.xl}
      minWidth={modalWidths.xl}
      withCloseIcon={false}
      onClose={() => navigate(-1)}
      renderContent={(modal) => (
        <Fragment>
          <TopActions>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                data-testid="kanban-kenshin-issueDetails-issueId"
                variant="h6"
                gutterBottom
                component="div"
                style={{
                  fontWeight: 'bold',
                  marginTop: '5px',
                  marginLeft: '14px',
                  textAlign: 'right',
                  alignSelf: 'stretch',
                }}>
                {issue.title}
              </Typography>
              <Tooltip
                title="Issue ID copied to clipboard."
                open={openCopyButtonTooltip}
                arrow
                leaveDelay={200}>
                <IconButton
                  onClick={() => handleCopyClick(issue.title)}
                  aria-label="copy">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <TopActionsRight>
              <IssueOptionsMenu
                setIssueShareConfirmModalOpen={setIssueShareConfirmModalOpen}
                shareIssueConfirmModalBodyText={shareIssueConfirmModalBodyText}
                shareIssueConfirmFunc={shareIssueConfirmFunc}
                shareIssueCancelFunc={shareIssueCancelFunc}
                shareIssueServiceType={ServiceType.KENSHIN}
                canDeleteIssue={
                  issue.progress === IssueStatus.NEWTASK && isClient
                }
                setIssueDeleteConfirmModalOpen={() => {
                  setConfirmModalWithConsentOpen(true);
                }}
              />
              <IconButton onClick={modal.close} aria-label="close">
                <CloseIcon />
              </IconButton>
            </TopActionsRight>
          </TopActions>
          <Box sx={{ margin: 3 }}>
            <Stack spacing={2}>
              <Info
                issue={issue}
                isClient={isClient}
                isTaskManager={isTaskManager}
                setConfirmModalInfo={setConfirmModalInfo}
                setConfirmModalUpdateData={setConfirmModalUpdateData}
                setConfirmModalOpen={setConfirmModalOpen}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Table
                  issue={issue}
                  doctors={doctors}
                  setBatchEditModalIsOpen={setBatchEditModalIsOpen}
                  setSelectedCases={setSelectedCases}
                  isClient={isClient}
                  setViewModalOpen={setViewModalOpen}
                  setViewportWebUrls={setViewportWebUrls}
                />
              </LocalizationProvider>
            </Stack>
          </Box>

          {/* Irregular */}
          <Modal
            open={viewportModalOpen}
            onClose={() => setViewModalOpen(false)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <ViewportModalPaper>
              <Viewport
                dicomFilePaths={viewportWebUrls}
                close={() => setViewModalOpen(false)}
                isKenshin={true}
              />
            </ViewportModalPaper>
          </Modal>

          {/* Delete Issue Modal */}
          <ConfirmModalWithConsent
            isOpened={confirmModalWithConsentOpen}
            onCancel={() => setConfirmModalWithConsentOpen(false)}
            onConfirm={handleDelete}
            title={intl.get('issue_details_delete_issue_title')}
            bodyText={intl.get('issue_details_delete_issue_body')}
            confirmButtonText={intl.get('issue_details_button_delete')}
            cancelButtonText={intl.get('issue_details_button_cancel')}
          />

          <ConfirmModal
            isOpened={issueShareConfirmModalOpen}
            title={intl.get('issue_details_confirm_modal_title_share_issue')}
            bodyText={shareIssueConfirmModalBodyText.current}
            onConfirm={() => {
              shareIssueConfirmFunc.current();
            }}
            onCancel={() => {
              shareIssueCancelFunc.current();
            }}
            confirmButtonColor="info"
            confirmButtonText={intl.get(
              'issue_details_confirm_modal_button_share_issue_confirm'
            )}
            cancelButtonColor="secondary"
            cancelButtonText={intl.get(
              'issue_details_confirm_modal_button_share_issue_cancel'
            )}
          />

          <ConfirmModal
            isOpened={confirmModalOpen}
            title={intl.get('issue_details_confirm_modal_title_update_issue')}
            bodyText={generateConfirmMessage(confirmModalInfo)}
            onConfirm={() => {
              updateIssue(confirmModalUpdateData);
              setConfirmModalOpen(false);
            }}
            onCancel={() => {
              setConfirmModalOpen(false);
            }}
            confirmButtonText={intl.get(
              'kanban_confirm_popup_window_button_yes'
            )}
            cancelButtonText={intl.get('kanban_confirm_popup_window_button_no')}
          />

          {/* Irregular */}
          <Modal
            open={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: zIndexValues.modal4,
            }}>
            <PreviewModalPaper>
              <Preview
                issue={issue}
                attachment={attachmentObjToBePreviewed}
                modalClose={() => setPreviewModalOpen(false)}
              />
            </PreviewModalPaper>
          </Modal>

          {batchEditModalIsOpen && (
            <KenshinCaseBatchEditModal
              isOpened={batchEditModalIsOpen}
              closeModal={closeBatchEditModal}
              selectedCases={selectedCases}
              doctors={doctors}
              updateLocalCase={updateLocalCase}
              setBatchEditModalIsOpen={setBatchEditModalIsOpen}
              updateLocalProjectIssues={updateLocalProjectIssues}
              issue={issue}
              updateIssue={updateIssue}
            />
          )}
        </Fragment>
      )}
    />
  );
};

export default KenshinIssueDetails;
